import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar } from '../../../components/ui/Avatar'
import { MoreVertical, Settings, FileText } from 'lucide-react'
import type { Agent } from '../../../services/agentService'

interface AgentHoverCardProps {
    agent: Agent
    showOptions?: boolean
}

export const AgentHoverCard: React.FC<AgentHoverCardProps> = ({ agent, showOptions = true }) => {
    const navigate = useNavigate()

    return (
        <div className="w-[300px] bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-xl z-50">
            {/* Header: Avatar and Menu */}
            <div className="flex justify-between items-start mb-3">
                <Avatar
                    src={agent.avatar_url || undefined}
                    alt={agent.name}
                    size="lg" // 56px approx
                    className="w-16 h-16 border-2 border-white dark:border-black"
                />
                {showOptions && (
                    <button className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white transition-colors">
                        <MoreVertical className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* Info */}
            <div className="mb-4">
                <div className="flex items-center gap-1 mb-0.5">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                        {agent.name}
                    </h3>
                    {/* Verified Badge */}
                    <svg className="w-5 h-5" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#1d9bf0" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"></path>
                    </svg>
                </div>
                <div className="text-gray-500 mb-1">
                    @{agent.handle || agent.name.toLowerCase().replace(/\s+/g, '')}
                </div>
                <div className="text-gray-500 dark:text-gray-400 text-sm mb-3">
                    Master
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 text-sm mb-4">
                    <span className="flex items-center gap-1">
                        <span className="text-gray-900 dark:text-white font-medium">v{agent.version || '1.0.0'}</span>
                        <span className="text-gray-500">Version</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="text-gray-900 dark:text-white font-medium">N/A</span>
                        <span className="text-gray-500">Quality</span>
                    </span>
                </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/agentes/${agent.handle || agent.name}`)
                    }}
                    className="flex-1 flex items-center justify-center gap-2 h-10 rounded-full border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white font-semibold hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                >
                    <FileText className="w-4 h-4" />
                    Perfil
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/agentes/${agent.handle || agent.name}/configuracao`)
                    }}
                    className="flex-1 flex items-center justify-center gap-2 h-10 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                    <Settings className="w-4 h-4" />
                    Config
                </button>
            </div>
        </div>
    )
}
